'use client';

import {
	Button,
	DataList,
	For,
	Group,
	HStack,
	IconButton,
	InputAddon,
	Separator,
	SimpleGrid,
	Stack,
	Text,
} from '@chakra-ui/react';
import { NumberInputField, NumberInputRoot, Tooltip } from '@packages/chakra-v3';

import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import type { GetProductBySlugResponse } from '../../react-query/products';
import { BsExclamationCircleFill } from 'react-icons/bs';
import React from 'react';
import { DefaultStyle, TwoColumnAutoGrid } from '@packages/wastic-shared';
import ProductPrice from '../ProductPrice/ProductPrice';

type OnClickAddToCart = {
	productId: string;
	quantity:  number;
};

type ProductDetailProps = {
	product:          GetProductBySlugResponse;
	onClickAddToCart: ({ productId, quantity }: OnClickAddToCart) => void;
};

// eslint-disable-next-line complexity
const ProductDetail = ({ product, onClickAddToCart }: ProductDetailProps) => {
	const locale = useLocale() as 'en' | 'th';
	const t = useTranslations();

	const price = locale === 'th' ? product.price : product.priceUsd;
	const discountedPrice = locale === 'th' ? product.discount?.discountPrice : product.discount?.discountPriceUsd;
	const currency = locale === 'th' ? 'THB' : 'USD';

	const isInStock = product.stock > 0;
	const [ quantity, setQuantity ] = useState<number>(isInStock ? 1 : 0);
	const emptySpec = ![
		!product.frameShape, !product.frameColor, !product.lensColor, !product.isPolarized,
	].every(Boolean);

	const productSpec = [
		{
			label: t('ProductDetailPage.frameShape'),
			value: product.frameShape,
		},
		{
			label: t('ProductDetailPage.frameColor'),
			value: product.frameColor,
		},
		{
			label: t('ProductDetailPage.lensColor'),
			value: product.lensColor,
		},
		{
			label: t('ProductDetailPage.polarized'),
			value: product.isPolarized ? 'Yes' : 'No',
		},
	].filter(spec => spec.value);

	return (
		<Stack gap={DefaultStyle.SECTION_GAP}>
			<Stack gap={DefaultStyle.SMALL_GAP}>
				<Stack gap={DefaultStyle.NO_GAP}>
					<Text color={'gray.500'} textStyle={'p4'} textTransform={'uppercase'}>
						{product.collectionName}
					</Text>
					<Text as={'h2'} textStyle={'h2'}>
						{product.name}
					</Text>
					<HStack alignItems={'center'}>
						<ProductPrice
							price={{
								amount:     price,
								discounted: discountedPrice,
								currency,
							}}
						/>
						<Tooltip content={t('ProductDetailPage.priceToolTip')}>
							<IconButton
								aria-label={'Info'}
								size={'md'}
								variant={'ghost'}
								w={'fit-content'}
							>
								<BsExclamationCircleFill />
							</IconButton>
						</Tooltip>
					</HStack>
				</Stack>
				<Stack gap={DefaultStyle.SMALL_GAP} separator={<Separator borderBottom={'1px solid gray'} />}>
					<Text color={'gray.500'} fontSize={'16px'} fontWeight={600}>
						Description
					</Text>
					<Text textStyle={'p3'} whiteSpace={'pre-wrap'}>
						{product.description ? product.description[locale as never] : ''}
					</Text>
				</Stack>
			</Stack>
			<TwoColumnAutoGrid
				alignItems={'center'}
				gap={DefaultStyle.MEDIUM_GAP}
				minChildWidth={'200px'}
				rowGap={DefaultStyle.SMALL_GAP}
			>
				{isInStock && (
					<Group attached>
						<NumberInputRoot
							bg={'white'}
							flex={1}
							max={product.stock}
							min={1}
							size={'lg'}
							value={quantity.toString()}
							onValueChange={e => setQuantity(e.valueAsNumber)}
						>
							<NumberInputField />
						</NumberInputRoot>
						<InputAddon>of {product.stock}</InputAddon>
					</Group>
				)}
				<Button
					disabled={!isInStock}
					flex={1}
					size={'md'}
					onClick={() => onClickAddToCart({
						productId: product.id,
						quantity,
					})}
				>
					{isInStock ? 'Add to Cart' : 'Out of stock'}
				</Button>
			</TwoColumnAutoGrid>
			{emptySpec && (
				<Stack gap={DefaultStyle.SMALL_GAP} separator={<Separator borderBottom={'1px solid gray'} />} w={'full'}>
					<Text color={'gray.500'} fontSize={'16px'} fontWeight={600}>
						{t('ProductDetailPage.productSpec')}
					</Text>
					<DataList.Root>
						<SimpleGrid columns={2} gap={DefaultStyle.SMALL_GAP} w={'full'}>
							<For each={productSpec}>
								{spec => (
									<DataList.Item gap={DefaultStyle.NO_GAP}>
										<DataList.ItemLabel color={'gray.600'} fontSize={'14px'} fontWeight={300}>
											{spec.label}
										</DataList.ItemLabel>
										<DataList.ItemValue fontSize={'16px'} fontWeight={600}>
											{spec.value}
										</DataList.ItemValue>
									</DataList.Item>
								)}
							</For>
						</SimpleGrid>
					</DataList.Root>
				</Stack>
			)}
		</Stack>
	);
};

export default ProductDetail;
